/* multibuy-cart-groups — the theme's one entry point for multibuy bundles.
 *
 * groupCart(cart) takes the cart.json.liquid payload (or anything with the
 * same `multibuy_grouping` block and `items[]`) and answers which units sit
 * in which bundle, priced so every box adds up to its offer price:
 *
 *   1. assets/multibuy-grouping.js (global MultibuyGrouping — generated from
 *      the multibuy-app repo, never edited here) reproduces the checkout
 *      function's grouping from the inputs the function reads, then checks
 *      it against the discounts Shopify actually allocated.
 *   2. When that check fails (stale sync, stale bundle file) or the module is
 *      missing, groupByAllocations() reads the real bundles off Shopify's
 *      discount applications instead — best effort: titles and totals are
 *      not unique identifiers, so an ambiguous application stays ungrouped.
 *   3. Nothing resolvable: every unit ungrouped, with a console warning that
 *      names the missing input. A "2 for $20" box never shows prices that do
 *      not add up.
 *
 * Both carts call it: cart-group-engine.js (drawer-v3, through
 * rules.multibuy.groupCart so the engine stays free of `window`) and cart.js
 * multibuyCheck (legacy cart page / side cart).
 *
 * Payload fields read, all emitted by cart.json.liquid:
 *   cart.multibuy_grouping   { currency, local_date, shop_date, offers }
 *   cart.items[]             key, quantity, original_price, active_offers,
 *                            gift_with_purchase,
 *                            discount_allocations[] { title, amount, total_allocated_amount }
 *
 * Result — the module's GroupingResult, each group item carrying its slice:
 *   groups[]     { offerKey, title, offerQuantity, bundles, priceCents,
 *                  originalPriceCents, savingsCents,
 *                  items: [{ lineId, quantity, originalCents, priceCents }] }
 *   ungrouped[]  { lineId, quantity }
 * groups[] are in checkout order, ONE PER BUNDLE (bundles is always 1): a
 * line can sit in several groups (qty 3 on "2 for $20" is 2 in one and 1 in
 * another; qty 4 is two groups of 2).
 */
(function (global) {
  'use strict';

  function num(value) {
    var n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  function warn() {
    if (global.console && global.console.warn) global.console.warn.apply(global.console, ['[multibuy-cart-groups]'].concat([].slice.call(arguments)));
  }

  /**
   * The line's multi_custom.active_offers id list as offer keys — what the
   * sync wrote on the variant and what the checkout bundles on. [] for "-"
   * (synced, no offers), a missing field or a variant without the metafield.
   */
  function activeOfferKeys(item) {
    var value = item && typeof item.active_offers === 'string' ? item.active_offers : '';
    return value.split(',').map(function (key) { return key.trim(); }).filter(function (key) { return key && key !== '-'; });
  }

  function none(items) {
    return { groups: [], ungrouped: items.map(function (item) { return { lineId: item.key, quantity: num(item.quantity) }; }) };
  }

  /** The module's GroupingInput, from the payload. */
  function groupingInput(config, items) {
    return {
      currency: config.currency,
      localDate: config.local_date,
      shopDate: config.shop_date,
      // The blob is a string on the checkout wire; the payload carries the
      // json metafield's parsed value, so re-serialise it.
      offers: typeof config.offers === 'string' ? config.offers : JSON.stringify(config.offers || []),
      lines: items.map(function (item) {
        return {
          id: item.key,
          quantity: num(item.quantity),
          unitPriceCents: num(item.original_price),
          activeOffers: item.active_offers || null,
          isGift: item.gift_with_purchase === true,
        };
      }),
    };
  }

  /** The discounts Shopify applied, per line, for the module's check. */
  function allocationsByLine(items) {
    var out = {};
    items.forEach(function (item) {
      out[item.key] = (item.discount_allocations || []).map(function (a) { return { title: a.title, amountCents: num(a.amount) }; });
    });
    return out;
  }

  /**
   * One group per bundle. The module folds several bundles from ONE line into
   * a group with bundles > 1 (qty 4 on "2 for $20") and the allocation
   * fallback folds every bundle of one application into a group; the carts
   * show a box per bundle, so each such group is cut into `bundles` boxes of
   * offerQuantity units, filled most expensive units first as the checkout
   * fills them (ties in line order). Every box costs the offer price — the
   * last one takes any rounding so the boxes still add up to the group — and
   * its slices are left for priceGroups to price. A group whose units do not
   * make up its bundles is left alone.
   */
  function splitBundles(result, items) {
    var unitCents = {};
    items.forEach(function (item) { unitCents[item.key] = num(item.original_price); });
    var groups = [];
    result.groups.forEach(function (group) {
      var count = num(group.bundles);
      var size = num(group.offerQuantity);
      var units = group.items.reduce(function (s, entry) { return s + num(entry.quantity); }, 0);
      if (count <= 1 || size < 1 || units !== count * size) { groups.push(group); return; }
      var pool = group.items.map(function (entry, i) { return { lineId: entry.lineId, left: num(entry.quantity), unit: unitCents[entry.lineId] || 0, order: i }; });
      pool.sort(function (a, b) { return (b.unit - a.unit) || (a.order - b.order); });
      var boxPrice = Math.round(group.priceCents / count);
      var remaining = group.priceCents;
      for (var n = 0; n < count; n += 1) {
        var box = [];
        var slots = size;
        var original = 0;
        for (var i = 0; i < pool.length && slots > 0; i += 1) {
          if (pool[i].left <= 0) continue;
          var take = Math.min(pool[i].left, slots);
          box.push({ lineId: pool[i].lineId, quantity: take });
          pool[i].left -= take;
          slots -= take;
          original += take * pool[i].unit;
        }
        var price = n === count - 1 ? remaining : boxPrice;
        remaining -= price;
        groups.push({ offerKey: group.offerKey, title: group.title, offerQuantity: size, bundles: 1, items: box, priceCents: price, originalPriceCents: original, savingsCents: original - price });
      }
    });
    result.groups = groups;
    return result;
  }

  /**
   * Price each group item so the group adds up to priceCents: a single-line
   * group costs it outright; a mixed group splits its saving across the lines
   * by price, as Shopify does, and the last slice takes the rounding
   * (Shopify's own split can put that cent on another line — the group total
   * is the same). Items that already carry a price (the allocation fallback
   * knows the real per-line amounts) are left alone.
   */
  function priceGroups(result, items) {
    var unitCents = {};
    items.forEach(function (item) { unitCents[item.key] = num(item.original_price); });
    result.groups.forEach(function (group) {
      var remaining = group.priceCents;
      group.items.forEach(function (entry, i) {
        if (entry.priceCents !== undefined) return;
        var original = entry.quantity * (unitCents[entry.lineId] || 0);
        var share = group.originalPriceCents > 0 ? Math.round((group.savingsCents * original) / group.originalPriceCents) : 0;
        var price = i === group.items.length - 1 ? remaining : original - share;
        remaining -= price;
        entry.originalCents = original;
        entry.priceCents = price;
      });
    });
    return result;
  }

  // ------------------------------------------------------------- fallback

  /** Offer rules from the multibuy_rules blob (array or JSON string), by title, for one currency. */
  function rulesByTitle(offers, currency) {
    var list = offers;
    if (typeof list === 'string') {
      try { list = JSON.parse(list); } catch (e) { list = []; }
    }
    var out = {};
    (Array.isArray(list) ? list : []).forEach(function (o) {
      if (!o || (o.c || o.currencyCode) !== currency) return;
      var title = o.t || o.title || o.d || o.description || o.dk || o.discount_key;
      var q = parseInt(o.q !== undefined ? o.q : o.quantity, 10);
      var s = parseFloat(o.s !== undefined ? o.s : o.subtotal);
      if (!title || !(q >= 2) || !Number.isFinite(s)) return;
      if (!out[title]) out[title] = [];
      out[title].push({ key: o.k || o.key, title: title, quantity: q, subtotalCents: Math.round(s * 100) });
    });
    return out;
  }

  /**
   * How many units of each member sit in the application's bundle(s).
   * Shopify splits a bundle's fixed amount across its lines in proportion to
   * price, so alloc_i / total = units_i × price_i / bundleOriginal, with
   * bundleOriginal = total + n × subtotal for n bundles. The smallest n whose
   * unit counts come out whole AND price back to that original (a cent per
   * member of rounding) wins; null when none does — then the allocations are
   * not a multibuy bundle's (a stale cart, another discount under the same
   * title) and the lines stay ungrouped rather than in a box that does not
   * add up.
   */
  function unitsOf(members, totalCents, rule) {
    var maxUnits = members.reduce(function (s, m) { return s + m.quantity; }, 0);
    for (var n = 1; n * rule.quantity <= maxUnits; n += 1) {
      var original = totalCents + n * rule.subtotalCents;
      var units = [];
      var sum = 0;
      var priced = 0;
      var ok = true;
      for (var i = 0; i < members.length; i += 1) {
        var m = members[i];
        if (!m.unitPriceCents || !totalCents) { ok = false; break; }
        var exact = (m.amountCents * original) / (totalCents * m.unitPriceCents);
        var u = Math.round(exact);
        if (u < 1 || u > m.quantity || Math.abs(exact - u) > 0.2) { ok = false; break; }
        units.push(u);
        sum += u;
        priced += u * m.unitPriceCents;
      }
      if (ok && sum === n * rule.quantity && Math.abs(priced - original) <= members.length) return { units: units, bundles: n };
    }
    return null;
  }

  /**
   * The real bundles read off Shopify's discount applications. The checkout
   * function emits one discount per bundle and Shopify keeps one application
   * per discount, so lines whose allocations share an application (title +
   * total_allocated_amount) are one bundle. A title can name several
   * independent offers; an allocation is only used when the line's
   * active_offers identifies exactly one of them (a unique title needs no
   * keys, so older payloads still group).
   */
  function groupByAllocations(config, items) {
    var rules = rulesByTitle(config.offers, config.currency);
    var apps = {};
    var order = [];

    items.forEach(function (line, position) {
      var keys = activeOfferKeys(line);
      (line.discount_allocations || []).forEach(function (a) {
        var candidates = (a && rules[a.title]) || [];
        var matching = candidates.filter(function (candidate) { return keys.indexOf(candidate.key) !== -1; });
        var rule = candidates.length === 1 ? candidates[0] : matching.length === 1 ? matching[0] : null;
        var total = a && num(a.total_allocated_amount);
        if (!rule || !(total > 0) || !(num(a.amount) > 0)) return;
        var id = JSON.stringify([rule.key, a.title, total]);
        if (!apps[id]) { apps[id] = { rule: rule, totalCents: 0, members: [] }; order.push(id); }
        var app = apps[id];
        var member = app.members.filter(function (m) { return m.lineId === line.key; })[0];
        if (!member) {
          member = { lineId: line.key, position: position, quantity: num(line.quantity), unitPriceCents: num(line.original_price), amountCents: 0 };
          app.members.push(member);
        }
        member.amountCents += num(a.amount);
        // The application total is what Shopify allocated across all its
        // lines, i.e. the sum of the members' amounts — summed here so folded
        // applications stay consistent.
        app.totalCents += num(a.amount);
      });
    });

    var groups = [];
    var grouped = {};
    order.forEach(function (id) {
      var app = apps[id];
      var solved = unitsOf(app.members, app.totalCents, app.rule);
      if (!solved) return;

      var members = app.members.map(function (m, i) { return { lineId: m.lineId, quantity: solved.units[i], unitPriceCents: m.unitPriceCents, amountCents: m.amountCents, position: m.position }; });
      // Whole bundles from several lines under one application (the function
      // merges equal single-line bundles): one box per line, as the module does.
      var perLine = members.length > 1 && members.every(function (it) { return it.quantity % app.rule.quantity === 0; });
      var boxes = perLine ? members.map(function (it) { return [it]; }) : [members];

      boxes.forEach(function (box) {
        var original = 0;
        var savings = 0;
        box.forEach(function (it) { original += it.quantity * it.unitPriceCents; savings += it.amountCents; grouped[it.lineId] = (grouped[it.lineId] || 0) + it.quantity; });
        var units = box.reduce(function (s, it) { return s + it.quantity; }, 0);
        groups.push({
          offerKey: app.rule.key,
          title: app.rule.title,
          offerQuantity: app.rule.quantity,
          bundles: Math.max(1, Math.round(units / app.rule.quantity)),
          // Priced from the real allocation: exact per line, by construction.
          items: box.map(function (it) {
            var originalCents = it.quantity * it.unitPriceCents;
            return { lineId: it.lineId, quantity: it.quantity, originalCents: originalCents, priceCents: originalCents - it.amountCents };
          }),
          priceCents: original - savings,
          originalPriceCents: original,
          savingsCents: savings,
          position: Math.min.apply(null, box.map(function (it) { return it.position; })),
        });
      });
    });
    groups.sort(function (a, b) { return a.position - b.position; });
    groups.forEach(function (g) { delete g.position; });

    var ungrouped = [];
    items.forEach(function (line) {
      var left = num(line.quantity) - (grouped[line.key] || 0);
      if (left > 0) ungrouped.push({ lineId: line.key, quantity: left });
    });
    return { groups: groups, ungrouped: ungrouped };
  }

  // ------------------------------------------------------------ entry point

  function fromAllocations(config, items) {
    try {
      return priceGroups(splitBundles(groupByAllocations(config, items), items), items);
    } catch (error) {
      warn('allocation grouping failed; rendering ungrouped', error);
      return none(items);
    }
  }

  function groupCart(cart) {
    var items = (cart && cart.items) || [];
    if (!items.length) return none(items);

    var config = cart.multibuy_grouping;
    if (!config) {
      warn('no multibuy_grouping in the payload; rendering ungrouped');
      return none(items);
    }

    var module = global.MultibuyGrouping;
    if (!module) {
      warn('multibuy-grouping.js not loaded; grouping from the discount applications');
      return fromAllocations(config, items);
    }

    var input = groupingInput(config, items);
    var allocations = allocationsByLine(items);
    var outcome;
    try {
      outcome = module.groupMultibuyCartChecked(input, allocations);
    } catch (error) {
      warn('grouping failed; grouping from the discount applications', error, input);
      return fromAllocations(config, items);
    }
    if (!outcome.matched) {
      // Logged with the cart data: the app team needs it to rebuild the module.
      warn('groups do not match the discounts Shopify applied; grouping from the discount applications instead', { input: input, allocations: allocations, result: outcome.result });
      return fromAllocations(config, items);
    }
    return priceGroups(splitBundles(outcome.result, items), items);
  }

  global.MultibuyCartGroups = { groupCart: groupCart, activeOfferKeys: activeOfferKeys };
})(typeof window !== 'undefined' ? window : globalThis);
